import { z } from 'zod';
import { TelcoProductScopeSchema } from '../enums/telco.js';
import { IsoDateStringSchema, UuidV7Schema } from './common.js';

export const VerticaleSchema = z.object({
  id: UuidV7Schema,
  name: z.string().min(1).max(100),
  /**
   * Libellé **destiné au prospect** (SMS, textes clients) — le `name` est un
   * nom interne qui ne s'envoie pas tel quel (« conseiller expert Chien /
   * Chat »). Null/vide = fallback sur `name`.
   */
  smsLabel: z.string().max(60).nullable().optional(),
  slug: z.string().min(1).max(50),
  priority: z.number().int().min(0),
  icon: z.string().nullable().optional(),
  /** Prix de vente Hipto — renvoyé aux ADMINS seulement (absent sinon). */
  prixVente: z.number().min(0).optional(),
  /** Rétrocession au réseau — renvoyée aux ADMINS seulement (absente sinon). */
  commission: z.number().min(0).optional(),
  arguments: z.array(z.string()),
  active: z.boolean(),
  createdAt: IsoDateStringSchema,
  updatedAt: IsoDateStringSchema,
});
export type Verticale = z.infer<typeof VerticaleSchema>;

export const CreateVerticaleSchema = z.object({
  name: z.string().min(1).max(100),
  smsLabel: z.string().max(60).nullable().optional(),
  slug: z.string().min(1).max(50),
  priority: z.number().int().min(0).default(0),
  icon: z.string().nullable().optional(),
  prixVente: z.number().min(0).default(0),
  commission: z.number().min(0).default(0),
  arguments: z.array(z.string()).max(5).default([]),
});
export type CreateVerticaleInput = z.infer<typeof CreateVerticaleSchema>;

export const UpdateVerticaleSchema = CreateVerticaleSchema.partial().extend({
  active: z.boolean().optional(),
});
export type UpdateVerticaleInput = z.infer<typeof UpdateVerticaleSchema>;

/**
 * Lead destination after Collect routing — derived from the Collect/DALI
 * response (sk prefix). Persisted on `LeadDelivery` for reporting only.
 * No longer attached to `DeliveryPartner` — Collect owns the routing.
 */
export const DeliverySystemSchema = z.enum(['manager', 'hopti']);
export type DeliverySystem = z.infer<typeof DeliverySystemSchema>;

/** "HH:MM" 24h time. Empty string treated as null upstream. */
const TimeOfDaySchema = z
  .string()
  .regex(/^\d{2}:\d{2}$/, 'Format attendu HH:MM')
  .nullable()
  .optional();

export const DeliveryPartnerSchema = z.object({
  id: UuidV7Schema,
  name: z.string().min(1).max(200),
  revealText: z.string().max(2000),
  verticaleId: UuidV7Schema,
  weight: z.number().int().min(1).max(100),
  active: z.boolean(),
  /** Customer-facing brand name (e.g. "Orange", "EDF"). */
  brand: z.string().max(100).nullable().optional(),
  /** Internal callcenter name (e.g. "Armatis"). Not shown to prospects. */
  callcenter: z.string().max(100).nullable().optional(),
  /** Partner-specific sales arguments displayed at the Reveal step. */
  arguments: z.array(z.string().max(300)).max(10).default([]),
  /**
   * @deprecated Ancien levier de routage Manager (forwardé en `forcedSk`).
   * Le pipeline Manager est retiré (2026-08) : le champ n'est plus ni
   * saisissable ni transmis à Collect. Conservé en lecture seule parce que
   * la colonne DB reste en place (données historiques).
   */
  collectSk: z.string().min(1).max(200).nullable().optional(),
  /**
   * Hopti account id — forwarded as `forceAccountId` so Hopti dispatches the
   * lead to THIS account (bypasses prioritization). Seule voie de routage
   * depuis le retrait du pipeline Manager. Null = let Hopti pick from its
   * prioritization.
   */
  hoptiAccountId: z.string().min(1).max(50).nullable().optional(),
  /**
   * Numéro miroir : numéro entrant dédié callcenter que le prospect peut
   * composer pour joindre directement le partenaire. Affiché sur la fiche
   * lead tant que le statut n'est pas terminal. Format libre (E.164 ou
   * national avec/sans espaces). Null = pas de mirror configuré.
   */
  mirrorPhone: z.string().min(1).max(40).nullable().optional(),
  /**
   * Scope de routage Telco. `null` = wildcard (sert tous les scopes Telco) —
   * c'est le défaut au moment de l'introduction de la feature. Permet
   * d'introduire un Armatis `fibre_only` plus tard sans toucher au code ni
   * recréer Club Conso. Cf. `TelcoProductScope` côté enums.
   * Ignoré pour les partenaires d'autres verticales (`null` = no-op).
   */
  telcoProductScope: TelcoProductScopeSchema.nullable().optional(),
  /** Weekday opening time, HH:MM (24h). Null = no schedule defined. */
  openingStart: TimeOfDaySchema,
  openingEnd: TimeOfDaySchema,
  /**
   * Jours d'ouverture du partenaire. 1=lundi, …, 6=samedi. Le dimanche (0)
   * est strictement exclu côté front (constante métier). Default
   * `[1,2,3,4,5]` = lundi-vendredi.
   */
  openingDays: z.array(z.number().int().min(1).max(6)).default([1, 2, 3, 4, 5]),
  /**
   * Quota journalier de leads pour ce partenaire (reset minuit Europe/Paris).
   * Null = pas de cap. Quand atteint : exclu du tirage côté wizard, fallback
   * naturel sur les partenaires sans quota du même verticale.
   */
  dailyQuota: z.number().int().min(0).max(1000).nullable().optional(),
  /**
   * Compteur lecture-seule retourné par l'API : nombre de leads créés
   * aujourd'hui (00h00 Europe/Paris) chez ce partenaire. Sert au pacing
   * temporel et au hard cap côté `pickDeliveryPartner`.
   */
  deliveredToday: z.number().int().min(0).optional(),
  createdAt: IsoDateStringSchema,
  updatedAt: IsoDateStringSchema,
});
export type DeliveryPartner = z.infer<typeof DeliveryPartnerSchema>;

export const CreateDeliveryPartnerSchema = z.object({
  name: z.string().min(1).max(200),
  revealText: z.string().max(2000).default(''),
  verticaleId: UuidV7Schema,
  weight: z.number().int().min(1).max(100).default(100),
  brand: z.string().max(100).nullable().optional(),
  callcenter: z.string().max(100).nullable().optional(),
  arguments: z.array(z.string().max(300)).max(10).default([]),
  /**
   * Requis à la création (et non-nullable en update via `.partial()`) : tout
   * partenaire doit être épinglé Hopti depuis le retrait du pipeline Manager
   * (2026-08). L'invariant est tenu ICI, côté API — le disable du bouton
   * Enregistrer dans l'éditeur n'est que de l'UX (« never trust the client »).
   * Vérifié en prod au moment du durcissement : 0 partenaire sans
   * hopti_account_id, tous statuts confondus.
   */
  hoptiAccountId: z.string().min(1).max(50),
  mirrorPhone: z.string().min(1).max(40).nullable().optional(),
  telcoProductScope: TelcoProductScopeSchema.nullable().optional(),
  openingStart: TimeOfDaySchema,
  openingEnd: TimeOfDaySchema,
  openingDays: z.array(z.number().int().min(1).max(6)).default([1, 2, 3, 4, 5]),
  dailyQuota: z.number().int().min(0).max(1000).nullable().optional(),
});
export type CreateDeliveryPartnerInput = z.infer<typeof CreateDeliveryPartnerSchema>;

export const UpdateDeliveryPartnerSchema = CreateDeliveryPartnerSchema.partial().extend({
  active: z.boolean().optional(),
});
export type UpdateDeliveryPartnerInput = z.infer<typeof UpdateDeliveryPartnerSchema>;

/**
 * Volume par acheteur sur une fenêtre glissante — `GET
 * /configuration/delivery-partners/stats?days=N`, ADMIN SEULEMENT (écran
 * Configuration › Partenaires). Fiches non archivées créées sur la période ;
 * ventes = `COMMISSIONABLE_SALE_STATUSES` (vente acquise). Un acheteur sans
 * fiche sur la période est absent de `partners` (= 0).
 */
export const DeliveryPartnerStatsSchema = z.object({
  days: z.number().int().min(1),
  since: IsoDateStringSchema,
  partners: z.array(
    z.object({
      deliveryPartnerId: z.string(),
      leads: z.number().int().min(0),
      activatedSales: z.number().int().min(0),
    }),
  ),
});
export type DeliveryPartnerStats = z.infer<typeof DeliveryPartnerStatsSchema>;

/**
 * Marque du partenaire telle que le prospect la connaît : `brand`, sinon le
 * `name` interne débarrassé du call-center entre parenthèses (« Orange
 * (Armatis) » → « Orange »). Null si rien d'affichable. Une seule règle pour
 * la fiche, le Fil et les SMS — sinon le même prospect lit deux marques.
 */
export function deliveryPartnerBrand(
  dp: { name: string; brand: string | null } | null | undefined,
): string | null {
  if (!dp) return null;
  if (dp.brand) return dp.brand;
  const cleaned = dp.name.replace(/\s*\([^)]*\)\s*$/, '').trim();
  return cleaned.length > 0 ? cleaned : null;
}
